import { useCallback, useEffect, useState, type FormEvent } from 'react'
import SimpleToothChartPicker from '../../pages/manage/treatmenttable/components/treatments/SimpleToothChartPicker'
import LabOrderItemsEditor from './LabOrderItemsEditor'
import LabPatientPicker from './LabPatientPicker'
import type { CatalogCategory } from './labCatalog'
import {
  type InvoiceLineForm,
  type LabOrderFormValues,
  type LabPriceListItem,
  computeOrderTotals,
  todayIsoDate,
} from './labOrderFormUtils'

export type LabOrderPartnerOption = {
  id: string
  labId: string | null
  name: string
  address?: string
  phone?: string
  isLocal?: boolean
  /** The clinic's own lab, offered under "Modelled by" */
  isOwnLab?: boolean
}

export type DentistOption = { id: string; name: string }

/** What the server lets the clinic change now, from the order detail. */
export type EditableFields = { fields: string[]; reason: string }

const inputClass =
  'mt-1 w-full border border-slate-300 dark:border-slate-600 rounded-lg px-3 py-2 text-sm dark:bg-slate-800'
const labelClass = 'block text-sm font-medium text-slate-600 dark:text-slate-400'

const PHOTOS_HINT =
  "Add photos of the case — impressions, shade, the patient's smile."

const addFieldLabel =
  'mb-1.5 block text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400'
const addFieldControl =
  'h-10 w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm transition-colors focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100'

function Section({
  title,
  description,
  children,
}: {
  title: string
  description?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4 sm:p-5 space-y-4">
      <div>
        <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {title}
        </h3>
        {description ? (
          <p className="mt-1 text-xs text-slate-500">{description}</p>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function FormBlock({
  step,
  title,
  hint,
  children,
}: {
  step: number
  title: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50/70 p-4 dark:border-slate-700/80 dark:bg-slate-800/40">
      <div className="mb-4 flex items-start gap-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-bold text-white shadow-sm">
          {step}
        </span>
        <div className="min-w-0 pt-0.5">
          <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {title}
          </h4>
          {hint ? (
            <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{hint}</p>
          ) : null}
        </div>
      </div>
      {children}
    </div>
  )
}

function formatLkr(amount: number) {
  return `LKR ${(Number(amount) || 0).toLocaleString('en-LK')}`
}

export type ClinicDetails = {
  name: string
  phone?: string
  address?: string
}

function DetailsPanel({
  title,
  name,
  phone,
  address,
}: {
  title: string
  name: string
  phone?: string
  address?: string
}) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50/80 px-4 py-3 dark:border-slate-600 dark:bg-slate-800/50">
      <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">
        {title}
      </p>
      <dl className="grid gap-3 text-sm">
        <div>
          <dt className="text-slate-500">Name</dt>
          <dd className="font-medium text-slate-900 dark:text-slate-100">{name}</dd>
        </div>
        {phone ? (
          <div>
            <dt className="text-slate-500">Phone</dt>
            <dd className="text-slate-800 dark:text-slate-200">{phone}</dd>
          </div>
        ) : null}
        {address ? (
          <div>
            <dt className="text-slate-500">Address</dt>
            <dd className="text-slate-800 dark:text-slate-200">{address}</dd>
          </div>
        ) : null}
      </dl>
    </div>
  )
}

function LabDetailsPanel({ partner }: { partner: LabOrderPartnerOption }) {
  return (
    <DetailsPanel
      title="Lab details"
      name={partner.name}
      phone={partner.phone}
      address={partner.address}
    />
  )
}

type LabOrderFormProps = {
  partners: LabOrderPartnerOption[]
  values: LabOrderFormValues
  onChange: (values: LabOrderFormValues) => void
  onSubmit: (e: FormEvent) => void
  submitting: boolean
  submitLabel: string
  submittingLabel?: string
  onCancel?: () => void
  partnerLabel?: string
  clinicDetails?: ClinicDetails
  priceListItems?: LabPriceListItem[]
  priceListLoading?: boolean
  /** Care32 catalogue, for hand-added labs' "Not in the price list?" */
  catalog?: CatalogCategory[]
  patientError?: boolean
  /** Flatter layout for dialogs — lab first, fewer nested cards. */
  variant?: 'page' | 'modal'
  /** Hide bottom Cancel/Submit so a parent modal can own a sticky footer. */
  hideActions?: boolean
  formId?: string
  /** Lock patient (e.g. treatment lab order — patient comes from the treatment). */
  patientReadOnly?: boolean
  /** Optional photos picker, shown as its own section after the form fields. */
  photosField?: React.ReactNode
  /** Doctors for the Doctor dropdown */
  dentists?: DentistOption[]
  /** 'edit' locks the lab and patient, and every field the server says is locked */
  mode?: 'create' | 'edit'
  editable?: EditableFields
  /** Offer only this master category's items first (order from a treatment stage) */
  onlyCategoryId?: string
}

export default function LabOrderForm({
  partners,
  values,
  onChange,
  onSubmit,
  submitting,
  submitLabel,
  submittingLabel = 'Sending…',
  onCancel,
  partnerLabel = 'Lab',
  clinicDetails,
  priceListItems = [],
  priceListLoading = false,
  catalog = [],
  patientError = false,
  variant = 'page',
  hideActions = false,
  formId,
  patientReadOnly = false,
  photosField,
  dentists = [],
  mode = 'create',
  editable,
  onlyCategoryId = '',
}: LabOrderFormProps) {
  const isEdit = mode === 'edit'
  /** Locked on an edit when the server says so (lab accepted, order done…) */
  const locked = (field: string) => isEdit && !(editable?.fields ?? []).includes(field)
  const isModal = variant === 'modal'
  const totals = computeOrderTotals(values)
  const selectedPartner = partners.find((p) => p.id === values.labPartnerId)
  const isManualItems = selectedPartner?.isLocal === true

  function patch(partial: Partial<LabOrderFormValues>) {
    onChange({ ...values, ...partial })
  }

  const [toothError, setToothError] = useState(false)

  useEffect(() => {
    if (values.toothNos.length > 0) setToothError(false)
  }, [values.toothNos])

  function selectPartner(partnerId: string) {
    const partner = partners.find((p) => p.id === partnerId)
    onChange({
      ...values,
      labPartnerId: partnerId,
      labId: partner?.labId ? String(partner.labId) : '',
      invoiceItems: [],
    })
  }

  const ownLabs = partners.filter((p) => p.isOwnLab && p.id !== values.labPartnerId)
  const lineCategories = Array.from(
    new Map(
      values.invoiceItems.filter((l) => l.categoryId).map((l) => [l.categoryId as string, l.categoryName]),
    ),
  )
  const itemCount = values.invoiceItems.length
  const setLines = useCallback(
    (invoiceItems: InvoiceLineForm[]) => onChange({ ...values, invoiceItems }),
    [onChange, values],
  )
  const canSubmit = Boolean(values.labPartnerId) && !submitting

  function onPatientSelect(patient: string, patientMeta: string) {
    const next: Partial<LabOrderFormValues> = { patient, patientMeta }
    if (!values.caseTitle.trim()) {
      const regMatch = patient.match(/\(([^)]+)\)\s*$/)
      if (regMatch?.[1]) {
        next.caseTitle = `Case — ${regMatch[1]}`
      }
    }
    patch(next)
  }

  const patientField = (
    <>
      <LabPatientPicker
        patientLabel={values.patient}
        patientMeta={values.patientMeta}
        showError={patientError}
        readOnly={patientReadOnly || isEdit}
        onSelect={onPatientSelect}
        onClear={
          patientReadOnly || isEdit
            ? undefined
            : () => patch({ patient: '', patientMeta: '' })
        }
      />
      <input
        type="hidden"
        name="patient"
        value={values.patient}
        required
        readOnly
        aria-hidden
        tabIndex={-1}
      />
    </>
  )

  const caseDetailFields = (
    <div className="space-y-4">
      <div>
        <label htmlFor="case-title" className={addFieldLabel}>
          Case title <span className="text-primary">*</span>
        </label>
        <input
          id="case-title"
          required
          value={values.caseTitle}
          onChange={(e) => patch({ caseTitle: e.target.value })}
          placeholder="e.g. Upper zirconia crown"
          disabled={locked('caseTitle')}
          className={`${addFieldControl} disabled:bg-slate-100`}
        />
      </div>
      <div>
        <p className={addFieldLabel}>
          Teeth <span className="text-primary">*</span>
        </p>
        <div
          className={locked('toothNos') ? 'pointer-events-none opacity-60' : ''}
          aria-disabled={locked('toothNos')}
        >
          <SimpleToothChartPicker
            value={values.toothNos}
            required
            error={toothError}
            onChange={(teeth) => {
              setToothError(false)
              patch({ toothNos: teeth })
            }}
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="case-meta" className={addFieldLabel}>
            Work type
          </label>
          <input
            id="case-meta"
            value={values.caseMeta}
            onChange={(e) => patch({ caseMeta: e.target.value })}
            placeholder="e.g. Implant, screw-retained"
            disabled={locked('caseMeta')}
            className={`${addFieldControl} disabled:bg-slate-100`}
          />
        </div>
        <div>
          <label htmlFor="case-detail" className={addFieldLabel}>
            Notes for lab
          </label>
          <input
            id="case-detail"
            value={values.caseDetail}
            onChange={(e) => patch({ caseDetail: e.target.value })}
            placeholder="Material, special instructions…"
            disabled={locked('caseDetail')}
            className={`${addFieldControl} disabled:bg-slate-100`}
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="order-date" className={addFieldLabel}>
            Order date
          </label>
          <input
            id="order-date"
            type="date"
            value={values.orderDate}
            onChange={(e) => patch({ orderDate: e.target.value })}
            disabled={isEdit}
            className={`${addFieldControl} disabled:bg-slate-100`}
          />
        </div>
        <div>
          <label htmlFor="due-date" className={addFieldLabel}>
            Due date <span className="text-primary">*</span>
          </label>
          <input
            id="due-date"
            type="date"
            required
            value={values.dueDate}
            onChange={(e) => patch({ dueDate: e.target.value })}
            min={isEdit ? undefined : todayIsoDate()}
            disabled={locked('dueDate')}
            className={`${addFieldControl} disabled:bg-slate-100`}
          />
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="priority" className={addFieldLabel}>
            Priority
          </label>
          <select
            id="priority"
            value={values.priority}
            onChange={(e) => patch({ priority: e.target.value === 'Urgent' ? 'Urgent' : 'Normal' })}
            disabled={locked('priority')}
            className={`${addFieldControl} disabled:bg-slate-100 ${values.priority === 'Urgent' ? 'border-red-400 text-red-700' : ''}`}
          >
            <option value="Normal">Normal</option>
            <option value="Urgent">Urgent</option>
          </select>
        </div>
        <div>
          <label htmlFor="dentist" className={addFieldLabel}>
            Doctor
          </label>
          <select
            id="dentist"
            value={values.dentistId}
            onChange={(e) => patch({ dentistId: e.target.value })}
            disabled={locked('dentistId')}
            className={`${addFieldControl} disabled:bg-slate-100`}
          >
            <option value="">Not set</option>
            {dentists.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="modelled-by" className={addFieldLabel}>
            Modelled by <span className="text-primary">*</span>
          </label>
          {ownLabs.length === 0 ? (
            <p id="modelled-by" className={`${addFieldControl} flex items-center bg-slate-100 text-slate-700`}>
              {selectedPartner?.name || 'The lab'}
            </p>
          ) : (
            <select
              id="modelled-by"
              value={values.modelledByPartnerId || values.labPartnerId}
              onChange={(e) => patch({ modelledByPartnerId: e.target.value === values.labPartnerId ? '' : e.target.value })}
              disabled={locked('modelledByPartnerId')}
              className={`${addFieldControl} disabled:bg-slate-100`}
            >
              <option value={values.labPartnerId}>{selectedPartner?.name || 'The lab'}</option>
              {ownLabs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} (own lab)
                </option>
              ))}
            </select>
          )}
        </div>
        {lineCategories.length > 1 ? (
          <div>
            <label htmlFor="case-category" className={addFieldLabel}>
              Case category
            </label>
            <select
              id="case-category"
              value={values.caseCategoryId || lineCategories[0][0]}
              onChange={(e) => patch({ caseCategoryId: e.target.value })}
              disabled={locked('caseCategoryId')}
              className={`${addFieldControl} disabled:bg-slate-100`}
            >
              {lineCategories.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-slate-500">The case ID is numbered under this category.</p>
          </div>
        ) : null}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="shade" className={addFieldLabel}>
            Shade
          </label>
          <input
            id="shade"
            value={values.shade}
            onChange={(e) => patch({ shade: e.target.value })}
            placeholder="e.g. A3"
            maxLength={40}
            disabled={locked('shade')}
            className={`${addFieldControl} disabled:bg-slate-100`}
          />
        </div>
        <div>
          <label htmlFor="shade-mix" className={addFieldLabel}>
            Shade mix
          </label>
          <input
            id="shade-mix"
            value={values.shadeMix}
            onChange={(e) => patch({ shadeMix: e.target.value })}
            maxLength={40}
            disabled={locked('shadeMix')}
            className={`${addFieldControl} disabled:bg-slate-100`}
          />
        </div>
      </div>
    </div>
  )

  function handleSubmit(e: FormEvent) {
    if (!locked('toothNos') && !values.toothNos.length) {
      e.preventDefault()
      setToothError(true)
      return
    }
    onSubmit(e)
  }

  const labSelect = (
    <div className="space-y-3">
      <label className={labelClass} htmlFor="lab-partner">
        {partnerLabel} <span className="text-primary">*</span>
        <select
          id="lab-partner"
          required
          value={values.labPartnerId}
          onChange={(e) => selectPartner(e.target.value)}
          disabled={isEdit}
          className={`${inputClass} disabled:bg-slate-100`}
        >
          {partners.length === 0 ? (
            <option value="">No labs available</option>
          ) : null}
          {partners.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
              {p.isLocal ? ' (personal)' : ''}
            </option>
          ))}
        </select>
      </label>
      {isModal && selectedPartner ? (
        <p className="text-xs text-slate-500">
          {[selectedPartner.phone, selectedPartner.address]
            .filter(Boolean)
            .join(' · ') || 'Partner lab'}
          {clinicDetails?.name ? ` · From ${clinicDetails.name}` : ''}
        </p>
      ) : null}
    </div>
  )

  const itemsEditor = !values.labPartnerId ? (
    <p className="text-sm text-slate-500">Select a lab first.</p>
  ) : (
    <LabOrderItemsEditor
      registered={!isManualItems}
      labName={selectedPartner?.name}
      priceList={priceListItems}
      priceListLoading={priceListLoading}
      catalog={catalog}
      toothNos={values.toothNos}
      lines={values.invoiceItems}
      onChange={setLines}
      disabled={locked('invoiceItems')}
      onlyCategoryId={onlyCategoryId}
    />
  )
  const modalItemsBody = itemsEditor
  const itemsBody = itemsEditor

  const actions = hideActions ? null : (
    <div className="flex flex-col-reverse gap-3 border-t border-slate-200 pt-5 dark:border-slate-700 sm:flex-row sm:justify-end">
      {onCancel ? (
        <button
          type="button"
          onClick={onCancel}
          disabled={submitting}
          className="rounded-lg border border-slate-300 px-5 py-2.5 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700/50"
        >
          Cancel
        </button>
      ) : null}
      <button
        type="submit"
        disabled={!canSubmit}
        className="rounded-lg bg-gradient-to-r from-primary to-primaryLight px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-opacity hover:opacity-95 disabled:opacity-60 sm:min-w-[10rem]"
      >
        {submitting ? submittingLabel : submitLabel}
      </button>
    </div>
  )

  const lockBanner =
    isEdit && editable?.reason ? (
      <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
        {editable.reason}
      </p>
    ) : null

  if (isModal) {
    return (
      <form
        id={formId}
        onSubmit={handleSubmit}
        className="space-y-5"
      >
        {lockBanner}
        <section className="space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              1. Choose lab
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              The order will be sent to this partner.
            </p>
          </div>
          {labSelect}
        </section>

        <section className="space-y-3 border-t border-slate-100 pt-5 dark:border-slate-700/80">
          <div>
            <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
              2. {patientReadOnly ? 'Case details' : 'Patient & case'}
            </h3>
            <p className="mt-0.5 text-xs text-slate-500">
              {patientReadOnly
                ? 'Patient comes from this treatment — describe the lab work.'
                : 'Confirm the patient and describe the work.'}
            </p>
          </div>
          <div className="space-y-4">
            {patientField}
            {caseDetailFields}
          </div>
        </section>

        <section className="space-y-3 border-t border-slate-100 pt-5 dark:border-slate-700/80">
          <div className="flex flex-wrap items-end justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                3. Lab items
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                {isManualItems
                  ? 'From your price list for this lab; leave the price empty if you don\u2019t know it yet.'
                  : 'From the lab\u2019s price list; the lab sets the prices.'}
              </p>
            </div>
            {itemCount > 0 ? (
              <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-semibold text-primary">
                {itemCount} item{itemCount === 1 ? '' : 's'} ·{' '}
                {formatLkr(totals.finalTotal)}
              </span>
            ) : null}
          </div>
          {modalItemsBody}
        </section>

        {photosField ? (
          <section className="space-y-3 border-t border-slate-100 pt-5 dark:border-slate-700/80">
            <div>
              <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                4. Photos{' '}
                <span className="font-normal text-slate-400">(optional)</span>
              </h3>
              <p className="mt-0.5 text-xs text-slate-500">
                {PHOTOS_HINT}
              </p>
            </div>
            {photosField}
          </section>
        ) : null}

        {actions}
      </form>
    )
  }

  return (
    <form id={formId} onSubmit={handleSubmit} className="space-y-5">
      <Section
        title="Case & patient"
        description="Select the patient, describe the lab work, and set expected dates."
      >
        <div className="space-y-4">
          <FormBlock
            step={1}
            title="Patient"
            hint="Search existing patients or add a new one — then tap to select."
          >
            {patientField}
          </FormBlock>

          <FormBlock
            step={2}
            title="Case details"
            hint="What work is being sent to the lab, and when you need it back."
          >
            {caseDetailFields}
          </FormBlock>
        </div>
      </Section>

      <Section title={partnerLabel}>
        {labSelect}
        <div className="grid gap-3 lg:grid-cols-2">
          {clinicDetails?.name ? (
            <DetailsPanel
              title="Clinic (sender)"
              name={clinicDetails.name}
              phone={clinicDetails.phone}
              address={clinicDetails.address}
            />
          ) : null}
          {selectedPartner ? <LabDetailsPanel partner={selectedPartner} /> : null}
        </div>
      </Section>

      <Section title="Lab item">{itemsBody}</Section>

      {photosField ? (
        <Section title="Photos (optional)" description={PHOTOS_HINT}>
          {photosField}
        </Section>
      ) : null}

      {actions}
    </form>
  )
}
